import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { toast } from 'sonner'

import { Textarea } from '@/components/common/form/textarea'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { useAuthStore } from '@/features/sessao/store/useAuthStore'
import { CallTechUserReport } from '@/services/backend/config/entities'

import { ChamadoTecnicoService } from '../../services/chamado-tecnico.service'

type Props = {
  data: CallTechUserReport
  onSuccess: (data: CallTechUserReport) => void
}

function useFormHook({ data, onSuccess }: Props) {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const form = useForm<Parameters<typeof ChamadoTecnicoService.edit>[0]>({
    defaultValues: data,
  })

  const onSubmit = form.handleSubmit(async (data) => {
    if (!user) return toast.error('Usuário não encontrado')

    try {
      const res = await ChamadoTecnicoService.edit({
        ...data,
        techUserId: user.id,
      })
      onSuccess(res)
      queryClient.invalidateQueries([
        'useGetCallDetails',
        { callId: data.callId },
      ])
    } catch {
      // Error toast is already shown by the API connector interceptor
    }
  })

  return { form, onSubmit }
}

function CreateForm({ data, onSuccess }: Props) {
  const { form, onSubmit } = useFormHook({ data, onSuccess })

  const [checkList, setCheckList] = useState({
    evidence: false,
    products: false,
  })

  const allChecked = checkList.products && checkList.evidence

  return (
    <Form {...form}>
      <form onSubmit={onSubmit}>
        <div className="flex flex-col gap-4">
          <Textarea
            field_control={form.control}
            field_name="description"
            field_label="Parecer técnico"
          />

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={checkList.evidence}
              onCheckedChange={() =>
                setCheckList((current) => ({
                  ...current,
                  evidence: !current.evidence,
                }))
              }
            />
            Anexei todas as evidências
          </label>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={checkList.products}
              onCheckedChange={() =>
                setCheckList((current) => ({
                  ...current,
                  products: !current.products,
                }))
              }
            />
            Realizei o consumo de produtos corretamente
          </label>

          <div className="mt-8 flex justify-between">
            <Button type="submit" disabled={!allChecked}>
              Encerrar atendimento
            </Button>
          </div>
        </div>
      </form>
    </Form>
  )
}

export default function Component({ data, onSuccess }: Props) {
  const { user } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)

  if (user?.id !== data.techUserId) return null

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>Encerrar atendimento</Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar</DialogTitle>
        </DialogHeader>

        <CreateForm
          data={data}
          onSuccess={(res) => {
            setIsOpen(false)
            onSuccess(res)
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
